import { useState } from "react";

import { SectionLabel } from "../SectionLabel";
import { DealIdeaCard } from "./DealIdeaCard";
import { IndustryPill } from "./IndustryPill";

const SIGNUP_URL = "https://mycouponbook.ca/signup/form";

const industries = [
    { id: "hotels", label: "Hotels", icon: "/images/hotel-2923328-1.svg" },
    { id: "cafe", label: "Café", icon: "/images/coffee-cup-7931536-1.svg" },
    { id: "retail", label: "Retail", icon: "/images/retail.svg" },
    { id: "entertainment", label: "Entertainment", icon: "/images/entertainment.svg" },
    { id: "restaurant", label: "Restaurant", icon: "/images/restaurant.svg" },
    { id: "auto", label: "Auto", icon: "/images/vector-9.svg" },
    { id: "home-services", label: "Home Services", icon: "/images/vector-11.svg" },
    { id: "spa-wellness", label: "Spa & Wellness", icon: "/images/vector-12.svg" },
    { id: "pet-stores", label: "Pet Stores", icon: "/images/pet-shop-6312282-1.svg" },
  ];

const dealIdeasMap: Record<
  string,
  { title: string; description: string }[]
> = {
  hotels: [
    {
      title: "20% Off Mid-Week Stays",
      description: "Drive occupancy on slow nights by attracting flexible travelers and staycationers.",
    },
    {
      title: "Free Breakfast with a 2-Night Booking",
      description: " Increase average stay length and keep guests on-property for future dining.",
    },
    {
      title: " $50 Dining Credit on Direct Bookings",
      description: "Avoid third-party commissions and drive immediate spend at your in-house restaurant.",
    },
  ],
  cafe: [
    {
      title: "Buy One, Get One 50% Off (2 PM - 5 PM)",
      description: "Turn the afternoon lull into a rush by encouraging coworkers to visit together.",
    },
    {
      title: "Free Pastry with Any Large Drink",
      description: "Introduce your high-margin food menu to build profitable new buying habits.",
    },
    {
      title: " $2 Off Any Breakfast Combo",
      description: " Turn morning commuters into loyal regulars by building a daily breakfast routine.",
    },
  ],
  retail: [
    {
      title: "Save $15 When You Spend $75+",
      description: "Instantly boost average order value by giving shoppers a reason to add one more item.",
    },
    {
      title: "Buy 2, Get 1 Free on Clearance",
      description: "Move stale inventory fast to free up valuable shelf space and cash flow.",
    },
    {
      title: "10% Off Entire Purchase (Followers Only)",
      description: "Trade a small discount for a loyal app follower you can market to all year for free.",
    },
  ],
  entertainment: [
    {
      title: "Half-Price Tuesdays",
      description: "Transform your deadest night into a community staple and build weekly habits.",
    },
    {
      title: "4-Person Family Pass for $40",
      description: "Attract groups who will spend significantly more on high-margin food and drinks.",
    },
    {
      title: "Buy 1 Hour, Get 30 Mins Free",
      description: "Keep guests in the venue longer, increasing the likelihood of concession sales.",
    },
  ],
  restaurant: [
    {
      title: "Free Appetizer with 2 Entrees (Mondays)",
      description: "Pack the dining room on slow nights and guarantee parties of two or more.",
    },
    {
      title: "$15 Express Lunch Combo",
      description: "Win the local lunch rush by attracting busy office workers who need a fast, high-value meal.",
    },
    {
      title: "Half-Price Bottles of Wine with Dinner",
      description: "Draw in date-night crowds while driving massive sales on full-priced meals.",
    },
  ],
  auto: [
    {
      title: "$39.99 Standard Oil Change",
      description: "Get new customers into the shop for routine maintenance to identify larger repair needs.",
    },
    {
      title: "Free Vacuum with Premium Wash",
      description: "Easily upsell customers from a basic exterior wash to a high-margin premium package.",
    },
    {
      title: "$20 Off Seasonal Tire Install",
      description: "Capture seasonal urgency and open the door for brake checks and fluid top-ups.",
    },
  ],
  "home-services": [
    {
      title: "$50 Off Any Repair Service Call",
      description: "Break the friction of upfront costs and get your technician through the front door.",
    },
    {
      title: "$99 Seasonal HVAC Tune-Up",
      description: "Fill your schedule during shoulder seasons and catch major repairs early.",
    },
    {
      title: "15% Off Full Home Deep Clean",
      description: "Lower the barrier on high-priced services while maintaining incredible profit margins.",
    },
  ],
  "spa-wellness": [
    {
      title: "$20 Off Next-Day Massages",
      description: "Use a flash sale to fill empty slots and keep your therapists booked and earning.",
    },
    {
      title: "Free Deep Conditioning with Color Service",
      description: "Highlight a high-margin add-on they’ll happily pay full price for next time.",
    },
    {
      title: "2-for-1 Pedicures on Wednesdays",
      description: "Double your customer base and fill seats by turning a solo trip into a social outing.",
    },
  ],
  "pet-stores": [
    {
      title: "15% Off Premium Dog Food",
      description: "Win their recurring food business, and they’ll buy their toys and accessories from you too.",
    },
    {
      title: "Buy 2 Get 1 Free on Treats",
      description: "Drive volume on high-margin inventory and significantly boost total checkout value.",
    },
    {
      title: "$10 Off Full Grooming Service",
      description: "Give pet owners the extra push they need to switch from their current groomer to you.",
    },
  ],
};

export const DealIdeasSection = () => {
  const [selectedIndustry, setSelectedIndustry] = useState("restaurant");

  const cards = dealIdeasMap[selectedIndustry];

  return (
    <section className="w-full bg-[#F7EFEE] px-[16px] sm:px-6 lg:px-[80px] pt-14 lg:pt-[72px] pb-12 lg:pb-[50px]">
      <div className="mx-auto w-full max-w-[1620px] text-center">
        <SectionLabel>Proven Offers</SectionLabel>

        <h2 className="mt-6 lg:mt-10 text-[#333] font-normal leading-[1.2] tracking-[-1.5px] text-[32px] sm:text-[44px] lg:text-[64px]">
          Deal Ideas for Your Industry
        </h2>

        <p className="mt-[8px] lg:mt-3 text-[#333] text-[15px] lg:text-[22px]">
          Get inspired. See what works for businesses like yours.
        </p>

        <div className="mt-8 lg:mt-[46px] flex flex-wrap justify-center gap-[8px] 2xl:gap-[12px]">
          {industries.map((industry) => (
            <IndustryPill
              key={industry.id}
              label={industry.label}
              icon={industry.icon}
              active={selectedIndustry === industry.id}
              onClick={() => setSelectedIndustry(industry.id)}
            />
          ))}
        </div>

        <div className="mt-10 lg:mt-[86px] grid grid-cols-1 md:grid-cols-3 gap-[16px] lg:gap-4 text-left">
          {cards.map((card) => (
            <DealIdeaCard key={card.title} title={card.title} description={card.description} />
          ))}
        </div>

        <a
          href={SIGNUP_URL}
          className="inline-block mt-10 lg:mt-[50px] rounded-full bg-[#3A3A3A] px-8 lg:px-[46px] py-[16px] lg:py-[22px] text-white text-[15px] lg:text-[20px] hover:bg-[#222] transition-colors"
        >
          Tap to publish a coupon based on your goal.
        </a>
      </div>
    </section>
  );
};
